// Pure TS Layer: React 非依存
// Cognito Identity Pool から一時 IAM 資格を取得する。
// 1. GetId で IdentityId を取得 (キャッシュ)
// 2. GetCredentialsForIdentity で AccessKey/Secret/Session/Expiration を取得
// 3. IoT Policy を AttachPolicy で identity に紐付け (初回のみ、副作用)

import {
  AttachPolicyCommand,
  IoTClient,
} from '@aws-sdk/client-iot';
import {
  CognitoIdentityClient,
  GetCredentialsForIdentityCommand,
  GetIdCommand,
} from '@aws-sdk/client-cognito-identity';
import type { IotAuthConfig } from './cognitoConfig';

export type AwsCredentials = {
  readonly accessKeyId: string;
  readonly secretAccessKey: string;
  readonly sessionToken: string;
  readonly expiration: Date;
};

export type CredentialsBundle = {
  readonly identityId: string;
  readonly credentials: AwsCredentials;
};

// 同一ブラウザセッション内で IdentityId / Credentials をキャッシュ
let cachedIdentityId: string | null = null;
let cachedCredentials: AwsCredentials | null = null;
// IoT Policy attach 済みフラグ (idempotent だが API 呼び出し節約のため)
const attachedPolicyFor = new Set<string>();

const EXPIRATION_BUFFER_MS = 5 * 60 * 1000; // 5 分のバッファで期限切れ前に再取得

function credentialsExpired(creds: AwsCredentials | null): boolean {
  if (!creds) return true;
  return creds.expiration.getTime() - EXPIRATION_BUFFER_MS < Date.now();
}

/**
 * Cognito Identity Pool から一時 IAM 資格を取得する。
 * 期限切れ前なら同じ資格を返す (キャッシュ)。
 */
export async function getIotCredentials(config: IotAuthConfig): Promise<CredentialsBundle> {
  const idClient = new CognitoIdentityClient({ region: config.region });

  // 1. IdentityId 取得 (キャッシュなければ)
  if (!cachedIdentityId) {
    const getIdResponse = await idClient.send(
      new GetIdCommand({ IdentityPoolId: config.identityPoolId }),
    );
    if (!getIdResponse.IdentityId) {
      throw new Error('Cognito GetId did not return IdentityId');
    }
    cachedIdentityId = getIdResponse.IdentityId;
  }

  // 2. Credentials 取得 (期限切れ or 未取得なら)
  if (credentialsExpired(cachedCredentials)) {
    const credsResponse = await idClient.send(
      new GetCredentialsForIdentityCommand({ IdentityId: cachedIdentityId }),
    );
    const c = credsResponse.Credentials;
    if (!c?.AccessKeyId || !c.SecretKey || !c.SessionToken || !c.Expiration) {
      throw new Error('Cognito GetCredentialsForIdentity returned incomplete credentials');
    }
    cachedCredentials = {
      accessKeyId: c.AccessKeyId,
      secretAccessKey: c.SecretKey,
      sessionToken: c.SessionToken,
      expiration: c.Expiration,
    };
  }

  if (!cachedCredentials) {
    throw new Error('Cognito credentials were not loaded after refresh attempt');
  }
  return { identityId: cachedIdentityId, credentials: cachedCredentials };
}

/**
 * 現在の identity に IoT Core Policy を attach する (idempotent)。
 * IoT MQTT 接続/購読を承認するために必須。
 */
export async function ensureIotPolicyAttached(config: IotAuthConfig): Promise<void> {
  const { identityId, credentials } = await getIotCredentials(config);
  const key = `${config.iotPolicyName}::${identityId}`;
  if (attachedPolicyFor.has(key)) return;

  const iotClient = new IoTClient({
    region: config.region,
    credentials: {
      accessKeyId: credentials.accessKeyId,
      secretAccessKey: credentials.secretAccessKey,
      sessionToken: credentials.sessionToken,
    },
  });

  try {
    await iotClient.send(
      new AttachPolicyCommand({
        policyName: config.iotPolicyName,
        target: identityId,
      }),
    );
    attachedPolicyFor.add(key);
  } catch (e) {
    // 既に attach 済みの場合 ResourceAlreadyExistsException が返ることがある (idempotent と見做す)
    const message = e instanceof Error ? e.message : String(e);
    if (message.includes('already') || message.includes('Already')) {
      attachedPolicyFor.add(key);
      return;
    }
    throw e;
  }
}

/** テスト用: キャッシュをリセット */
export function resetCognitoCacheForTest(): void {
  cachedIdentityId = null;
  cachedCredentials = null;
  attachedPolicyFor.clear();
}
